import { $t } from '@/i18n';
import { computed } from '@vue/composition-api';
import dependencyLoader from '@utils/dependencyLoader';
import moduleRegistry from '@utils/dependencyLoader/module-registry';
import { useStore } from '@/store';
import uploader from '@utils/upload';
import { message } from '@utils/wuji-message';
import hashString, { getRandomString } from '@utils/hashString';
import { cloneDeep, isEqual } from 'lodash';
export const buildAndUploadDependency = async (projectInfo, newPackageJson) => {
    const { bundle, packageJson } = await dependencyLoader.save({
        packageJson: newPackageJson,
    });
    if (!bundle.js && !bundle.css) {
        return { bundle: { js: '', css: '' }, packageJson: '' };
    }
    const allDeps = dependencyLoader.getInfo().allDependencies;
    const hashStrBase = (newPackageJson || projectInfo.dependency?.packageJson || '') + allDeps.sort().join(',');
    const hash = await hashString(hashStrBase, 10);
    const bundlePath = { js: '', css: '' };
    const uploadFile = async (file, filename, type) => {
        const host = encodeURIComponent(location.hostname).replaceAll('.', '_');
        const prefix = `wuji_project_deps/${host}/${projectInfo.id}`;
        try {
            return await uploader.newUploadFile({
                file,
                pathPrefix: prefix,
                filename,
                projectId: projectInfo.id,
            });
        }
        catch (e) {
            if (e.message.includes('Overwriting existing files is not allowed')) {
                const prefix = projectInfo.dependency.bundle?.js?.split('/').slice(0, -1)
                    .join('/');
                if (prefix) {
                    return `${prefix}/bundle-${hash}.${type}`;
                }
                return await uploadFile(file, `${getRandomString(4)}-${filename}`, type);
            }
        }
    };
    const fetchContent = async (urlOrContent) => {
        if (urlOrContent.startsWith('http://') || urlOrContent.startsWith('https://')) {
            const resp = await fetch(urlOrContent);
            if (!resp.ok)
                throw new Error(`Failed to fetch bundle: ${resp.statusText}`);
            return resp.text();
        }
        return urlOrContent;
    };
    const checkOrUploadFile = async (urlOrContent, type) => {
        if (!urlOrContent) {
            return '';
        }
        const content = await fetchContent(urlOrContent);
        return await uploadFile(new File([content], `bundle-${hash}.${type}`), `bundle-${hash}.${type}`, type);
    };
    bundlePath.js = await checkOrUploadFile(bundle.js, 'js');
    bundlePath.css = await checkOrUploadFile(bundle.css, 'css');
    return {
        bundle: bundlePath,
        packageJson,
    };
};
const useDepLoader = () => {
    const store = useStore();
    const projectInfo = computed(() => {
        if (window.RUNTIME_CONFIGS) {
            return store.state.runtime.project;
        }
        return store.state.project.projectInfo;
    });
    const currentBranch = computed(() => store.state.branch.currentBranch?.branch);
    const loadDependencies = async (dependencies, framework = 'vue3') => {
        if (!dependencies)
            return;
        const { bundle, pages } = projectInfo.value.dependency || { bundle: { js: '' }, pages: {} };
        await dependencyLoader.init(framework, pages);
        await moduleRegistry.load({ bundle });
    };
    const updateDependency = async (dependencies, pageId) => {
        if (!dependencies)
            return;
        if (!projectInfo.value.dependency) {
            projectInfo.value.dependency = {
                pages: {},
            };
        }
        const oldDeps = projectInfo.value.dependency.pages[pageId] || [];
        if (!dependencyLoader.hasBundled() && isEqual(oldDeps, Array.from(dependencies))) {
            return;
        }
        const newDependency = cloneDeep(projectInfo.value.dependency);
        newDependency.pages[pageId] = Array.from(dependencies);
        try {
            message.loading({ content: $t('k_95a6aab0'), duration: 0 });
            await dependencyLoader.load(pageId, Array.from(dependencies));
            const { bundle, packageJson } = await buildAndUploadDependency(projectInfo.value);
            newDependency.bundle = bundle;
            newDependency.packageJson = packageJson;
            if (isEqual(newDependency, projectInfo.value.dependency)) {
                return;
            }
            await store.dispatch('fetchProjectInfo', { projectId: projectInfo.value.id, branchId: currentBranch.value });
            await new Promise((resolve) => {
                setTimeout(() => {
                    resolve(true);
                }, 0);
            });
            await store.dispatch('updateProjectInfo', { muted: true, id: projectInfo.value.id, dependency: newDependency });
            projectInfo.value.dependency = newDependency;
        }
        catch (e) {
            message.error($t('k_a9ccf0cc'));
            console.error(`updateDependency fail ${e?.message} ${e?.stack}`, e);
            throw e;
        }
        finally {
            message.closeAll();
        }
    };
    return {
        loadDependencies,
        updateDependency,
    };
};
export default useDepLoader;
